// The supported-language vocabulary read, moved from
// `src/hooks/query/useQuerySupportedLanguages.ts` (now a delegate) with the
// session-header modal cores. Body is byte-identical to the
// original apart from the auth gate, which is now a parameter.
//
// ### Only the base query moved, and that is the whole W3c core split
//
// The old path also exports three DERIVED wrappers —
// `useQueryInputSupportedLanguages`, `useQueryOutputSupportedLanguages` and
// `useQueryDictationInputLanguages`. They stay app-side, calling this core,
// because they filter through `@/utils/supportedLanguageFilters`, whose homing
// is explicitly a separate decision: the `ui--session-header` exception rows
// that block on this carve ask for that module to be "homed on a specifier
// ruling in `docs/monorepo-ui-floor-rulings/`" as their OTHER falsifier. A
// package may not import it and this ticket may not rule on it, so the fetch
// core comes here and the filters stay where the ruling will find them.
//
// ### Why the auth gate is a parameter and not the config member
//
// Ruling 32 left the INNER hooks' contract untouched: `isAuthenticated` reaches
// `useQuerySessionByIdAuthenticated` and `useQueryTeamMemberSessionById` as a
// delegate-resolved param, and only the branching COMPOSITION reads the context
// member. This is a leaf, so it takes the param — and there is a measured
// reason not to reach for the context here. Unlike the four cohort cores, this
// hook never called `useCombinedQueriesTeamGeneralInfo`, and 17 importers sit
// outside the session surfaces entirely (dictation settings, the new-user
// creation page, the Epic Haiku panel, chat). Reading the context there would
// newly mount `GET /joined-teams` and `GET /teams/{id}` at each of them. The
// value passed is still the provider-aware verdict — `useIsAuthenticated()`,
// not `!!token` — which is the semantics ruling 32's round 2 settled.

import { useQuery } from '@tanstack/react-query';

import { LanguageCode } from '@heidi/schema/ml-scribe';

import { GET_SUPPORTED_LANGUAGES, type LanguageType } from '../../contracts';
import { getSupportedLanguages } from '../api/languages';

const orderPriority = [LanguageCode.auto, LanguageCode.en, LanguageCode.multi];

/**
 * `orderBy(data, [isPriority, 'name'], ['desc', 'asc'])`, hand-rolled.
 *
 * The original called lodash's `orderBy`, and `lodash-es` is ESM-only and
 * cannot enter package source (`useMutateSubmitNoteFeedback`'s header records
 * the same refusal, and `@heidi/workspace` hand-rolled `isEmpty` for it): a
 * `transformIgnorePatterns` allowlist here would have to be repeated in every
 * suite that consumes `@heidi/session/data`, `@heidi/patients` among them.
 *
 * The three semantics that had to be reproduced, rather than approximated —
 * `useQuerySupportedLanguages.test.ts` pins each:
 *
 *  - `'desc'` over the boolean puts the priority codes FIRST (`true > false`).
 *  - `'asc'` over `name` is lodash's `compareAscending`, a raw relational
 *    comparison — NOT `localeCompare`. `'Zulu' < 'afar'` by codepoint, and
 *    swapping in `localeCompare` reorders the list.
 *  - The sort is STABLE, and equal-priority equal-name pairs keep server order.
 *    `Array.prototype.sort` has been stable since ES2019, so no index tiebreak
 *    is needed; `orderBy` returns a new array, so the response is not mutated.
 */
function orderSupportedLanguages(data: LanguageType[]): LanguageType[] {
  return [...data].sort((a, b) => {
    const aIsPriority = orderPriority.includes(a.code);
    const bIsPriority = orderPriority.includes(b.code);
    if (aIsPriority !== bIsPriority) {
      return aIsPriority ? -1 : 1;
    }
    if (a.name === b.name) {
      return 0;
    }
    return a.name < b.name ? -1 : 1;
  });
}

export function useQuerySupportedLanguages({
  isAuthenticated,
  enabled = true,
}: {
  /** The provider-aware `isAuthenticated` verdict, delegate-resolved. */
  isAuthenticated: boolean;
  /**
   * A caller's OWN gate, ANDed with the auth verdict. Defaults to `true`, so
   * every existing caller is unchanged.
   *
   * It exists because this query has no `staleTime`, so where it is mounted
   * decides how often `GET /language/supported-languages` is issued. Callers that
   * used to mount it behind a user interaction — the session-header language
   * picker is the one that needed this — cannot express that as a mount any more
   * once the surface reading the list is a prop-taking component: the component
   * has to stay mounted for its own close animation, so the laziness has to be
   * the query's rather than the tree's.
   */
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [GET_SUPPORTED_LANGUAGES],
    queryFn: async () => {
      const { data } = await getSupportedLanguages();

      if (data) {
        const sortedData = orderSupportedLanguages(data);
        return sortedData;
      }
    },
    enabled: isAuthenticated && enabled,
  });
}
